import React from 'react';

import { Checkbox, Divider, Group, Paper, Select, Stack, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import AthleteStatusSelect from 'components/form-fields/select-fields/AthleteStatusSelect';
import KiloAlert from 'components/KiloAlert';
import dayjs from 'dayjs';

import { useCancelModal } from '../../CancelModalContext';

const cancellationReasonOptions = [
  'Price',
  'Moved/Travel Distance',
  'Scheduling',
  'Injury/Medical',
  'Admin',
];

const CancellationSettingsStep: React.FC = () => {
  const { form, athletePackage, isDesktop } = useCancelModal();

  const isUpcoming = athletePackage.status === 'UPCOMING';
  const packageEndDate = athletePackage.endDate ? dayjs(new Date(athletePackage.endDate)) : null;

  return (
    <Stack spacing='lg'>
      <Paper p='md' radius='md' bg='gray.0' withBorder>
        <Stack spacing={4}>
          <Text size='sm' fw={600}>
            How cancellation works
          </Text>
          <Text size='sm' color='dimmed'>
            Set when this package should end and update the athlete’s status.
          </Text>
        </Stack>
      </Paper>

      {packageEndDate && (
        <KiloAlert w='100%' variant='filled' variantStyle='warning'>
          {athletePackage.autoRenew
            ? `This package is set to renew on ${packageEndDate.format('MMMM D, YYYY')}.`
            : `This package is set to end on ${packageEndDate.format('MMMM D, YYYY')}.`}
        </KiloAlert>
      )}

      {athletePackage.child && (
        <KiloAlert w='100%' variant='filled' variantStyle='warning'>
          This selected package has created an upcoming renewal. <br />
          Canceling this package will delete the upcoming renewal package. <br />
          Any invoices with billing activity (refunds pending, bank payments in progress, etc) will be
          preserved.
        </KiloAlert>
      )}

      <Stack spacing='xs'>
        <Text size='sm' fw={500}>
          When to cancel
        </Text>
        <Stack spacing='xs'>
          {!isUpcoming && (
            <Paper
              p='sm'
              radius='md'
              withBorder
              onClick={() => {
                form.setFieldValue('cancelNow', true);
                form.setFieldValue('cancellationDate', null);
              }}
              sx={theme => ({
                cursor: 'pointer',
                borderColor: form.values.cancelNow ? theme.colors.red[5] : theme.colors.gray[3],
                backgroundColor: form.values.cancelNow ? theme.colors.red[0] : 'transparent',
                transition: 'border-color 150ms ease, background-color 150ms ease',
              })}
            >
              <Group noWrap spacing='sm'>
                <Checkbox
                  checked={form.values.cancelNow}
                  onChange={e => {
                    form.setFieldValue('cancelNow', e.currentTarget.checked);
                    if (e.currentTarget.checked) form.setFieldValue('cancellationDate', null);
                  }}
                  color='red'
                  onClick={e => e.stopPropagation()}
                />
                <Stack spacing={0}>
                  <Text size='sm' fw={500} lh={1.3}>
                    Cancel immediately
                  </Text>
                  <Text size='xs' color='dimmed'>
                    Takes effect as soon as you confirm
                  </Text>
                </Stack>
              </Group>
            </Paper>
          )}

          <Paper
            p='sm'
            radius='md'
            withBorder
            onClick={() => form.setFieldValue('cancelNow', false)}
            sx={theme => ({
              cursor: 'pointer',
              borderColor: !form.values.cancelNow ? theme.colors.blue[5] : theme.colors.gray[3],
              backgroundColor: !form.values.cancelNow ? theme.colors.blue[0] : 'transparent',
              transition: 'border-color 150ms ease, background-color 150ms ease',
            })}
          >
            <Stack spacing='sm'>
              <Group noWrap spacing='sm'>
                <Checkbox
                  checked={!form.values.cancelNow}
                  onChange={e => form.setFieldValue('cancelNow', !e.currentTarget.checked)}
                  onClick={e => e.stopPropagation()}
                />
                <Stack spacing={0}>
                  <Text size='sm' fw={500} lh={1.3}>
                    Schedule a date
                  </Text>
                  <Text size='xs' color='dimmed'>
                    Package stays active until the chosen date
                  </Text>
                </Stack>
              </Group>

              {!form.values.cancelNow && (
                <DatePickerInput
                  popoverProps={{
                    position: isDesktop ? 'left' : 'bottom',
                    shadow: 'xl',
                    withinPortal: true,
                    zIndex: 1000,
                  }}
                  minDate={isUpcoming ? new Date(athletePackage.startDate) : new Date()}
                  maxDate={athletePackage.endDate ? new Date(athletePackage.endDate) : undefined}
                  onClick={e => e.stopPropagation()}
                  {...{ placeholder: 'Select date' }}
                  {...form.getInputProps('cancellationDate')}
                />
              )}
            </Stack>
          </Paper>
        </Stack>
      </Stack>

      <Divider />

      <Stack spacing='md'>
        <Text size='sm' fw={500}>
          Cancellation details
        </Text>
        <AthleteStatusSelect
          label='Athlete Status Upon Cancel'
          description='Change occurs on cancellation date'
          type='onEnd'
          withinPortal
          {...form.getInputProps('athleteStatusOnCancel')}
        />
        <Select
          label='Cancellation Reason'
          placeholder='Select a reason'
          data={cancellationReasonOptions}
          withinPortal
          {...form.getInputProps('cancellationReason')}
        />
      </Stack>
    </Stack>
  );
};

export default CancellationSettingsStep;
